/**
 * Route Registry — Pure data constants shared between app code and build plugins.
 * 
 * IMPORTANT: This file must have ZERO imports from i18n, React, or any library.
 * It is consumed by both browser-context code (seo.ts) and Node-context build
 * plugins (vite-plugins/sitemap.ts). Adding any framework import will break
 * the build pipeline.
 */

// ============================================================================
// DOMAIN
// ============================================================================

/** Production domain for canonical URLs */
export const SEO_DOMAIN = "https://visexperience.com";

// ============================================================================
// SUPPORTED LANGUAGES
// ============================================================================

/**
 * All supported language codes (20 languages).
 * Order: EN first, then alphabetical by code.
 */
export const SUPPORTED_LANGUAGES: readonly string[] = [
  "en", "ar", "de", "el", "es", "fi", "fr", "hi", "id", "it",
  "ja", "ko", "nl", "pl", "pt", "sv", "th", "tr", "vi", "zh",
] as const;

// ============================================================================
// PUBLIC PAGE ROUTES
// ============================================================================

export interface PageRoute {
  /** URL path segment (empty string = home) */
  path: string;
  /** Sitemap priority 0.0–1.0 */
  priority: number;
  /** Sitemap change frequency */
  changefreq: string;
}

/**
 * Single source of truth for all 19 public page routes.
 * Used by: App.tsx routing, sitemap generation, SEO metadata, hreflang injection.
 */
export const PUBLIC_PAGE_ROUTES: readonly PageRoute[] = [
  { path: "", priority: 1.0, changefreq: "weekly" },
  { path: "government-solutions", priority: 0.9, changefreq: "monthly" },
  { path: "technology-platform", priority: 0.8, changefreq: "monthly" },
  { path: "operational-delivery", priority: 0.8, changefreq: "monthly" },
  { path: "global-presence", priority: 0.8, changefreq: "monthly" },
  { path: "tourism-trade-support", priority: 0.7, changefreq: "monthly" },
  { path: "case-studies", priority: 0.7, changefreq: "weekly" },
  { path: "resources", priority: 0.7, changefreq: "weekly" },
  { path: "about", priority: 0.6, changefreq: "monthly" },
  { path: "contact", priority: 0.8, changefreq: "monthly" },
  { path: "procurement", priority: 0.8, changefreq: "monthly" },
  { path: "governance", priority: 0.6, changefreq: "monthly" },
  { path: "implementation", priority: 0.6, changefreq: "monthly" },
  { path: "data-privacy", priority: 0.5, changefreq: "yearly" },
  { path: "security", priority: 0.6, changefreq: "monthly" },
  { path: "faq", priority: 0.6, changefreq: "monthly" },
  { path: "legal-notice", priority: 0.3, changefreq: "yearly" },
  { path: "privacy-policy", priority: 0.3, changefreq: "yearly" },
  { path: "terms", priority: 0.3, changefreq: "yearly" },
] as const;
